import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils.js';
import { Button } from './primitives.js';
import { useExclusiveDropdownClose } from './searchable-multi-select.js';
import { ToastNotification } from './surface-primitives.js';

export interface ContextMenuActionItem {
  readonly type?: 'item' | undefined;
  readonly id?: string | undefined;
  readonly label: ReactNode;
  readonly icon?: ReactNode | undefined;
  readonly onSelect?: (() => void) | undefined;
  readonly copyText?: string | undefined;
  readonly copyToast?: string | undefined;
  readonly externalUrl?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly destructive?: boolean | undefined;
}

export interface ContextMenuDividerItem {
  readonly type: 'divider';
}

export type ContextMenuEntry =
  ContextMenuActionItem | ContextMenuDividerItem | false | null | undefined;

export type NormalizedContextMenuItem = ContextMenuActionItem | ContextMenuDividerItem;

export const CONTEXT_MENU_DIVIDER: ContextMenuDividerItem = { type: 'divider' };

export function copyTextToClipboard(value: string): void {
  if (!value || typeof navigator === 'undefined') return;
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    void navigator.clipboard.writeText(value);
  }
}

export function openExternalHttpsUrl(url: string): void {
  if (typeof window === 'undefined') return;
  if (/^https?:\/\//i.test(url.trim())) {
    window.open(url.trim(), '_blank', 'noopener,noreferrer');
  }
}

/**
 * Filters falsy entries and collapses leading, trailing, or consecutive dividers
 * so conditional sections never leave orphaned separator lines.
 */
export function normalizeContextMenuEntries(
  entries: readonly ContextMenuEntry[],
): NormalizedContextMenuItem[] {
  const result: NormalizedContextMenuItem[] = [];
  for (const entry of entries) {
    if (!entry) continue;
    if (entry.type === 'divider') {
      if (result.length === 0 || result[result.length - 1]?.type === 'divider') {
        continue;
      }
      result.push(entry);
    } else {
      result.push(entry);
    }
  }
  while (result.length > 0 && result[result.length - 1]?.type === 'divider') {
    result.pop();
  }
  return result;
}

/**
 * Preserves the browser's native context menu when the user holds Shift,
 * right-clicks inside an editable form control, or has an active text selection.
 */
export function shouldAllowNativeContextMenu(event: {
  readonly shiftKey?: boolean;
  readonly target?: EventTarget | null;
}): boolean {
  if (event.shiftKey) return true;
  const target = event.target;
  if (target instanceof HTMLElement) {
    const tag = target.tagName;
    if (
      tag === 'INPUT' ||
      tag === 'TEXTAREA' ||
      tag === 'SELECT' ||
      target.isContentEditable ||
      target.closest('input, textarea, select, [contenteditable="true"]') !== null
    ) {
      return true;
    }
  }
  if (typeof window !== 'undefined' && typeof window.getSelection === 'function') {
    const selectedText = window.getSelection()?.toString().trim() ?? '';
    if (selectedText.length > 0) return true;
  }
  return false;
}

export function computeContextMenuPositionStyle(
  x: number,
  y: number,
  menuEl: HTMLElement | null,
): CSSProperties {
  const viewportW = typeof window !== 'undefined' ? window.innerWidth || 1024 : 1024;
  const viewportH = typeof window !== 'undefined' ? window.innerHeight || 768 : 768;
  const margin = 8;
  const rect = menuEl?.getBoundingClientRect();
  const menuW = rect && rect.width > 0 ? rect.width : 220;
  const menuH = rect && rect.height > 0 ? rect.height : 240;

  const clampedLeft = Math.max(margin, Math.min(x, viewportW - menuW - margin));
  const clampedTop = Math.max(margin, Math.min(y, viewportH - menuH - margin));

  return {
    position: 'fixed',
    zIndex: 85,
    left: `${Math.round(clampedLeft)}px`,
    top: `${Math.round(clampedTop)}px`,
  };
}

/**
 * Shared floating-menu dismissal hook used by both `ActionOverflowMenu` and `ContextMenu`.
 */
export function useFloatingMenuDismiss(
  open: boolean,
  onClose: () => void,
  refs: readonly RefObject<HTMLElement | null>[],
): void {
  useExclusiveDropdownClose(open, onClose);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as Node | null;
      if (target && refs.every((r) => !r.current?.contains(target))) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('mousedown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, refs]);
}

interface OpenContextMenuState {
  readonly x: number;
  readonly y: number;
  readonly items: readonly NormalizedContextMenuItem[];
  readonly label?: string | undefined;
}

export interface UseContextMenuResult {
  readonly isOpen: boolean;
  readonly openContextMenu: (
    event: ReactMouseEvent<Element>,
    entries: readonly ContextMenuEntry[],
    label?: string,
  ) => void;
  readonly bindContextMenu: (
    getEntries: () => readonly ContextMenuEntry[],
    label?: string,
  ) => (event: ReactMouseEvent<Element>) => void;
  readonly closeContextMenu: () => void;
  readonly contextMenuElement: ReactElement | null;
}

export function useContextMenu(): UseContextMenuResult {
  const [menuState, setMenuState] = useState<OpenContextMenuState | null>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const [copiedNotice, setCopiedNotice] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const closeContextMenu = useCallback(() => setMenuState(null), []);

  useFloatingMenuDismiss(menuState !== null, closeContextMenu, [menuRef]);

  useLayoutEffect(() => {
    if (!menuState || typeof window === 'undefined') {
      setMenuStyle({});
      return;
    }
    const updatePosition = (): void => {
      setMenuStyle(computeContextMenuPositionStyle(menuState.x, menuState.y, menuRef.current));
    };
    updatePosition();
    const closeOnViewportChange = (): void => setMenuState(null);
    window.addEventListener('resize', closeOnViewportChange);
    window.addEventListener('scroll', closeOnViewportChange, true);
    return () => {
      window.removeEventListener('resize', closeOnViewportChange);
      window.removeEventListener('scroll', closeOnViewportChange, true);
    };
  }, [menuState]);

  const openContextMenu = useCallback(
    (
      event: ReactMouseEvent<Element>,
      entries: readonly ContextMenuEntry[],
      label?: string,
    ): void => {
      if (shouldAllowNativeContextMenu(event)) return;
      const normalized = normalizeContextMenuEntries(entries);
      if (normalized.length === 0) return;
      event.preventDefault();
      event.stopPropagation();
      setMenuState({
        x: event.clientX,
        y: event.clientY,
        items: normalized,
        ...(label !== undefined ? { label } : {}),
      });
    },
    [],
  );

  const bindContextMenu = useCallback(
    (getEntries: () => readonly ContextMenuEntry[], label?: string) =>
      (event: ReactMouseEvent<Element>): void => {
        openContextMenu(event, getEntries(), label);
      },
    [openContextMenu],
  );

  const handleSelectItem = (item: ContextMenuActionItem): void => {
    closeContextMenu();
    if (item.copyText !== undefined) {
      copyTextToClipboard(item.copyText);
      setCopiedNotice(item.copyToast ?? 'Copied to clipboard.');
    }
    if (item.externalUrl !== undefined) {
      openExternalHttpsUrl(item.externalUrl);
    }
    item.onSelect?.();
  };

  const effectiveStyle =
    menuState !== null
      ? menuStyle.position !== undefined
        ? menuStyle
        : computeContextMenuPositionStyle(menuState.x, menuState.y, menuRef.current)
      : undefined;

  const menuPortal =
    menuState !== null ? (
      <div
        ref={menuRef}
        role="menu"
        aria-label={menuState.label ?? 'Context menu'}
        data-testid="context-menu"
        style={effectiveStyle}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        className={cn(
          'fixed z-[85] flex min-w-[200px] max-w-[280px] flex-col gap-0.5 rounded-control border border-border bg-surface p-1.5 shadow-modal [&>button]:w-full [&>button]:justify-start',
        )}
      >
        {menuState.items.map((entry, idx) =>
          entry.type === 'divider' ? (
            <div
              key={`divider-${String(idx)}`}
              role="separator"
              className="my-1 border-t border-subtle"
            />
          ) : (
            <Button
              key={entry.id ?? `item-${String(idx)}`}
              type="button"
              role="menuitem"
              size="sm"
              variant={entry.destructive ? 'destructive' : 'ghost'}
              disabled={entry.disabled}
              data-testid={entry.id ? `context-menu-item-${entry.id}` : undefined}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectItem(entry);
              }}
              className="text-left"
            >
              {entry.icon}
              <span className="truncate">{entry.label}</span>
            </Button>
          ),
        )}
      </div>
    ) : null;

  const renderedMenu =
    menuPortal !== null && typeof document !== 'undefined'
      ? createPortal(menuPortal, document.body)
      : menuPortal;

  const contextMenuElement =
    renderedMenu !== null || copiedNotice !== null ? (
      <>
        {renderedMenu}
        <ToastNotification
          data-testid="context-menu-toast"
          message={copiedNotice}
          onDismiss={() => setCopiedNotice(null)}
          autoHideMs={2500}
        />
      </>
    ) : null;

  return {
    isOpen: menuState !== null,
    openContextMenu,
    bindContextMenu,
    closeContextMenu,
    contextMenuElement,
  };
}
